import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { HierarchyItem } from '../../model/hierarchy-item';
import { Page } from '../../model/page';
import { AdminService, adminFailure } from '../../service/admin.service';
import { SeoService } from '../../service/seo.service';
import { MoneyPipe } from '../../money.pipe';
import { statusLabel } from './status-labels';

const SIZE = 20;

/**
 * The `product-hierarchy` page: every product line with its variants, and the income price - the
 * one number the shop owner edits from here rather than from the product page.
 */
@Component({
  selector: 'admin-hierarchy-page',
  imports: [FormsModule, RouterLink, MoneyPipe],
  host: {
    style: 'display: contents'
  },
  styles: `
    .admin-income input {
      width: 90px;
      border: 1px solid #e6e6e6;
      padding: 2px 6px;
    }
    `,
  template: `
    <section class="bg-title-page p-t-15 p-b-16 flex-col-c-m">
      <h1 class="l-text2 t-center">
        Ієрархія продуктів
      </h1>
    </section>

    <section class="cart bgwhite p-t-70 p-b-100">
      <div class="container">
        @if (failure(); as failure) {
          <p class="s-text5 text-danger text-center p-b-20">{{failure}}</p>
        }
        @if (hierarchy(); as hierarchy) {
          <div class="wrap-table-shopping-cart bgwhite">
            <table class="table-shopping-cart">
              <tbody>
                <tr class="table-head">
                  <th class="column-2">Товар</th>
                  <th class="column-3">Код</th>
                  <th class="column-3">Статус</th>
                  <th class="column-3 text-center">Ціна</th>
                  <th class="column-3 text-center">Вхідна ціна</th>
                  <th class="column-3 text-center">Баланс</th>
                </tr>
                @for (row of rows(); track row.item.id) {
                  <tr class="table-row admin-hierarchy-row">
                    <td class="column-2" [style.padding-left.px]="row.depth ? row.depth * 30 : null">
                      @if (row.item.uri; as uri) {
                        <a [routerLink]="uri">{{row.item.name}}</a>
                      } @else {
                        {{row.item.name}}
                      }
                    </td>
                    <td class="column-3">{{row.item.code}}</td>
                    <td class="column-3">{{row.label}}</td>
                    <td class="column-3 text-center">{{row.item.price | money}}</td>
                    <td class="column-3 text-center admin-income">
                      @if (!row.item.collection) {
                        <input type="number" step="0.01" min="0" [ngModel]="row.item.incomePrice"
                          [name]="'income-' + row.item.id" [attr.aria-label]="'Вхідна ціна ' + row.item.name"
                          (change)="saveIncomePrice(row.item, $any($event.target))">
                        @if (refused()?.id === row.item.id) {
                          <p class="s-text5 text-danger">{{refused()!.message}}</p>
                        }
                      }
                    </td>
                    <td class="column-3 text-center">{{row.item.totalBalance}}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <div class="pagination flex-m flex-w p-t-26">
            @for (number of pages(); track number) {
              <a class="item-pagination flex-c-m trans-0-4" [class.active-pagination]="number - 1 === hierarchy.number"
                [routerLink]="[]" [queryParams]="{ page: number - 1 }">
                {{number}}
              </a>
            }
          </div>
        } @else if (!failure()) {
          <p class="text-center">Завантаження…</p>
        }
      </div>
    </section>
    `
})
export class AdminHierarchyPageComponent {

  /** Bound from the `page` query parameter by `withComponentInputBinding`. */
  readonly page = input(0, { transform: (value: string | number) => Number(value) || 0 });

  private service = inject(AdminService);

  readonly hierarchy = signal<Page<HierarchyItem> | undefined>(undefined);
  readonly failure = signal<string | undefined>(undefined);
  readonly refused = signal<{ id: number; message: string } | undefined>(undefined);
  readonly rows = computed(() => (this.hierarchy()?.content.flatMap(item => this.flatten(item)) ?? [])
    .map(row => ({ ...row, label: this.label(row.item) })));
  readonly pages = computed(() =>
    Array.from({ length: this.hierarchy()?.totalPages ?? 0 }, (unused, index) => index + 1));

  constructor() {
    const seo = inject(SeoService);
    seo.apply({ title: 'Ієрархія продуктів', robots: 'noindex,nofollow' }, true);
    seo.openGraph(undefined);
    effect(() => {
      const page = this.page();
      this.refused.set(undefined);
      void this.load(page);
    });
  }

  /**
   * A line and every variant under it, as rows, so the table stays one flat list of `tr`. A colour
   * with sizes is a collection of its own, and the sizes under it are the products that are sold.
   */
  private flatten(item: HierarchyItem, depth = 0): { item: HierarchyItem; depth: number }[] {
    return [{ item, depth }, ...item.children.flatMap(child => this.flatten(child, depth + 1))];
  }

  /** A line split into colours is HIDDEN only so the catalogue lists the colours; it is on sale. */
  private label(item: HierarchyItem) {
    return item.status === 'HIDDEN' && item.children.length ? 'Лінійка' : statusLabel(item.status);
  }

  async saveIncomePrice(item: HierarchyItem, input: HTMLInputElement) {
    this.refused.set(undefined);
    if (input.value === '' || Number(input.value) < 0) {
      // Neither can be saved: min="0" does not stop a typed minus, and the api keeps its price for a blank
      input.value = String(item.incomePrice ?? '');
      return;
    }
    try {
      await this.service.edit(item.id, { incomePrice: Number(input.value) });
    } catch (error) {
      input.value = String(item.incomePrice ?? '');
      this.refused.set({
        id: item.id,
        message: adminFailure(error, 'Не вдалося зберегти вхідну ціну. Спробуйте ще раз.')
      });
      return;
    }
    await this.load(this.page());
  }

  private async load(page: number) {
    try {
      this.hierarchy.set(await this.service.hierarchy(page, SIZE));
      this.failure.set(undefined);
    } catch (error) {
      this.failure.set(adminFailure(error, 'Не вдалося завантажити товари. Спробуйте ще раз.'));
    }
  }

}
